"use client"

import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { useState, useEffect, type FormEvent } from "react"
import type React from "react"
import MODAL_NAMES from "../ModalName"
import { hideModalFromId } from "../../../shared/functions/ModalShow"
import { defaultUser, type IUser } from "../../../shared/models/User"
import { dateFormat_YY_MM_DD } from "../../shared/utils/utils"
import { type ITrainingRequest, defaultTrainingRequest } from "../../../shared/models/TrainingRequestModel"
import { Button } from "../../dashboard/company-dashboard/ui/button";
import { Label } from "../../dashboard/company-dashboard/ui/label";
import { Input } from "../../dashboard/company-dashboard/ui/input";
import { Textarea } from "../../dashboard/company-dashboard/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../dashboard/company-dashboard/ui/card";
import { X, Loader2, CheckCircle, FileText, Eye, Trash2, AlertCircle } from "lucide-react"

const CompleteCoarseModal = observer(() => {
  const { store, api } = useAppContext()
  const me = store.auth.me?.asJson
  const departments = store.department.all
  const [currentUser, setCurrentUser] = useState<IUser>({ ...defaultUser })
  const [courseRequest, setCourseRequest] = useState<ITrainingRequest>({
    ...defaultTrainingRequest,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    attachments?: string
    confirmation?: string
  }>({})

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }

  const [uploadProgress, setUploadProgress] = useState<number[]>([])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const uploadedFileLinks: string[] = []
      const progress: number[] = Array(files.length).fill(0)
      setUploadProgress(progress)

      await Promise.all(
        Array.from(files).map(async (file, index) => {
          const uploadedFileLink = await api.uploadManager.uploadQualificationToStorage(
            file,
            `trainingQualification/${me?.uid}`,
            (snapshot) => {
              const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
              progress[index] = percent
              setUploadProgress([...progress])
            },
          )
          uploadedFileLinks.push(uploadedFileLink)
        }),
      )

      setCourseRequest({
        ...courseRequest,
        attachment: [...courseRequest.attachment, ...uploadedFileLinks],
      })
      setUploadProgress([])
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    const newErrors: { attachments?: string; confirmation?: string } = {}
    if (courseRequest.attachment.length === 0) {
      newErrors.attachments = "Please upload at least one document."
    }
    if (!courseRequest.confirmation) {
      newErrors.confirmation = "You must confirm that the information is true and accurate."
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setLoading(false)
      return
    }

    try {
      if (store.trainingRequest.selected) {
        const updatedRequest: ITrainingRequest = {
          ...courseRequest,
          status: "Completed",
        }

        await api.trainingRequest.update(updatedRequest)
      } else {
        await api.trainingRequest.create(courseRequest)
      }
    } catch (error) {
      console.error("Error submitting training request:", error)
    } finally {
      setLoading(false)
      onCancel()
    }
  }

  const handleRemoveAttachment = (index: number) => {
    const updatedAttachments = [...courseRequest.attachment]
    updatedAttachments.splice(index, 1)
    setCourseRequest({
      ...courseRequest,
      attachment: updatedAttachments,
    })
  }

  const users = store.user.all.map((user) => user.asJson)
  const getName = (uid: string): string => {
    const currentUser = users.find((user) => user.uid === uid)
    return currentUser?.displayName || "Unknown Department"
  }

  useEffect(() => {
    if (store.trainingRequest.selected) {
      setCourseRequest(store.trainingRequest.selected)
    }
  }, [store.trainingRequest.selected])

  const onCancel = () => {
    store.personalDevelopment.clearSelected()
    hideModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_COMPLETE_TRAINING_MODAL)
  }

  const purposeOptions = ["Development", "Certification", "Other"]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="relative">
          <Button variant="ghost" size="icon" className="absolute right-4 top-4" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
          <CardTitle>Complete Training Request</CardTitle>
          <CardDescription>Mark your training as complete and upload proof of completion</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Employee Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Employee Information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={getName(courseRequest.uid)}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="text"
                    value={courseRequest.email || currentUser?.email || ""}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        email: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input id="department" value={getDepartmentName(courseRequest.department)} readOnly />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={courseRequest.phoneNumber}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        phoneNumber: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={courseRequest.location}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        location: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supervisor">Supervisor Name</Label>
                  <Input
                    id="supervisor"
                    value={getName(courseRequest.supervisor)}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        supervisor: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Course Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Course Information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="courseName">Course Name</Label>
                  <Input
                    id="courseName"
                    value={courseRequest.courseName}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        courseName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredDate">Preferred Date</Label>
                  <Input
                    id="preferredDate"
                    type="date"
                    value={dateFormat_YY_MM_DD(courseRequest.preferredDate)}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        preferredDate: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="offeredBy">Offered By</Label>
                  <Input
                    id="offeredBy"
                    value={courseRequest.offeredBy}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        offeredBy: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={courseRequest.duration}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        duration: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="courseLocation">Location</Label>
                  <Input
                    id="courseLocation"
                    value={courseRequest.courseLocation}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        courseLocation: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget</Label>
                  <Input
                    id="budget"
                    type="number"
                    value={courseRequest.budget}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        budget: e.target.valueAsNumber,
                      })
                    }
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="courseSummary">Brief Summary of Course Content</Label>
                  <Textarea
                    id="courseSummary"
                    rows={3}
                    value={courseRequest.courseSummary}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        courseSummary: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Purpose of Training Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Purpose of Training</h3>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={courseRequest.purpose}
                onChange={(e) =>
                  setCourseRequest({
                    ...courseRequest,
                    purpose: e.target.value,
                  })
                }
              >
                <option value="">Select purpose...</option>
                {purposeOptions.map((purpose) => (
                  <option key={purpose} value={purpose}>
                    {purpose}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Upload Document(s)</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#84cbe9]" />
                  Please attach proof of completion documents before completing
                </p>
              </div>
              <Input type="file" multiple onChange={handleFileChange} className="cursor-pointer" />

              {uploadProgress.length > 0 && uploadProgress.some((progress) => progress < 100) && (
                <div className="space-y-2">
                  {uploadProgress.map((progress, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>File {index + 1}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-green-500 transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {courseRequest.attachment.length > 0 ? (
                  <div className="space-y-2">
                    {courseRequest.attachment.map((url, index) => (
                      <div key={index} className="flex items-center justify-between rounded-lg border p-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Attachment {index + 1}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveAttachment(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No attachments found.</p>
                )}
                {errors.attachments && <p className="text-sm text-destructive">{errors.attachments}</p>}
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="confirmation"
                  checked={courseRequest.confirmation}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      confirmation: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="confirmation" className="text-sm font-normal">
                  I confirm that this information is true and accurate.
                </Label>
              </div>
              {errors.confirmation && <p className="text-sm text-destructive">{errors.confirmation}</p>}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Not Recommend
              </Button>
              <Button type="submit" disabled={loading} className="bg-[#4bb543] hover:bg-green-600">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
})

export default CompleteCoarseModal
